import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { toast } from '../utils/toast';
import { Breadcrumbs, BreadcrumbItem } from '../components/ui';
import {
  Radio,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  RefreshCw,
  Search,
  Eye,
  X,
  Copy,
  Check,
  Send,
  Clock,
  AlertOctagon,
} from 'lucide-react';

export interface WebhookDeliveryItem {
  id: string;
  webhook_config_id?: string;
  developer_webhook_id?: string;
  event_type: string;
  payload: Record<string, any> | string;
  status: 'pending' | 'success' | 'failed';
  attempts: number;
  last_attempt_at: string | null;
  response_status: number | null;
  response_body: string | null;
  error_message: string | null;
  created_at: string;
  updated_at: string;
  webhook_url: string;
  source_name: string;
  source_type: 'PRODUCT' | 'APPLICATION';
  client_name?: string;
  client_id?: string;
}

interface WebhookDeliveriesResponse {
  success: boolean;
  data: WebhookDeliveryItem[];
  metrics: {
    total: number;
    successful: number;
    failed: number;
    pending: number;
    dlq_count: number;
    success_rate: number;
  };
  pagination: {
    total: number;
    limit: number;
    offset: number;
  };
}

export const WebhookDeliveries: React.FC = () => {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<'ALL' | 'DLQ'>('ALL');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [eventTypeFilter, setEventTypeFilter] = useState('all');
  const [page, setPage] = useState(0);
  const pageSize = 20;

  // Selected item for Inspection Modal
  const [selectedDelivery, setSelectedDelivery] = useState<WebhookDeliveryItem | null>(null);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  // Multi-selection for DLQ Batch Reprocess
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  // Fetch Global Deliveries
  const { data: deliveriesResponse, isLoading, isFetching, refetch } = useQuery<WebhookDeliveriesResponse>({
    queryKey: ['admin-webhook-deliveries', activeTab, statusFilter, eventTypeFilter, searchTerm, page],
    queryFn: async () => {
      if (activeTab === 'DLQ') {
        const res = await api.get<{ success: boolean; data: WebhookDeliveryItem[]; total: number }>(
          '/portal/admin/webhooks/dlq',
          { params: { limit: 100 } }
        );
        // Map to standard response structure for seamless rendering
        return {
          success: true,
          data: res.data.data,
          metrics: {
            total: res.data.total,
            successful: 0,
            failed: res.data.total,
            pending: 0,
            dlq_count: res.data.total,
            success_rate: 0,
          },
          pagination: {
            total: res.data.total,
            limit: 100,
            offset: 0,
          },
        };
      }

      const res = await api.get<WebhookDeliveriesResponse>('/portal/admin/webhooks/deliveries', {
        params: {
          status: statusFilter,
          event_type: eventTypeFilter,
          search: searchTerm || undefined,
          limit: pageSize,
          offset: page * pageSize,
        },
      });
      return res.data;
    },
    refetchInterval: 30000,
  });

  // Single Delivery Retry Mutation
  const retryMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await api.post(`/portal/admin/webhooks/deliveries/${id}/retry`);
      return res.data;
    },
    onSuccess: () => {
      toast.success('Tentativa de re-envio disparada com sucesso.');
      queryClient.invalidateQueries({ queryKey: ['admin-webhook-deliveries'] });
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.error?.message || 'Falha ao reprocessar entrega.');
    },
  });

  // Batch DLQ Retry Mutation
  const batchRetryMutation = useMutation({
    mutationFn: async (ids?: string[]) => {
      const res = await api.post<{ success: boolean; message: string; retried_count: number }>(
        '/portal/admin/webhooks/dlq/batch-retry',
        { delivery_ids: ids && ids.length > 0 ? ids : undefined }
      );
      return res.data;
    },
    onSuccess: (data) => {
      toast.success(data.message || 'Lote de eventos reprocessado com sucesso.');
      setSelectedIds([]);
      queryClient.invalidateQueries({ queryKey: ['admin-webhook-deliveries'] });
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.error?.message || 'Falha ao reprocessar lote da DLQ.');
    },
  });

  const copyToClipboard = (text: string, key: string) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked && deliveriesResponse?.data) {
      setSelectedIds(deliveriesResponse.data.map((d) => d.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleToggleSelect = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const metrics = deliveriesResponse?.metrics || {
    total: 0,
    successful: 0,
    failed: 0,
    pending: 0,
    dlq_count: 0,
    success_rate: 100,
  };

  const deliveries = deliveriesResponse?.data || [];
  const totalItems = deliveriesResponse?.pagination?.total || 0;
  const totalPages = Math.ceil(totalItems / pageSize);

  const getStatusBadge = (status: string, httpCode: number | null) => {
    if (status === 'success') {
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-2xs font-bold font-mono bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-400">
          <CheckCircle2 className="w-3 h-3 text-emerald-500" />
          <span>{httpCode ? `${httpCode} OK` : '200 OK'}</span>
        </span>
      );
    }
    if (status === 'pending') {
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-2xs font-bold font-mono bg-blue-100 text-blue-800 dark:bg-blue-950/60 dark:text-blue-400">
          <Clock className="w-3 h-3 text-blue-500 animate-spin" />
          <span>Pendente</span>
        </span>
      );
    }
    // Failed
    if (httpCode && httpCode >= 400 && httpCode < 500) {
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-2xs font-bold font-mono bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-400">
          <AlertTriangle className="w-3 h-3 text-amber-500" />
          <span>HTTP {httpCode}</span>
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-2xs font-bold font-mono bg-rose-100 text-rose-800 dark:bg-rose-950/60 dark:text-rose-400">
        <XCircle className="w-3 h-3 text-rose-500" />
        <span>{httpCode ? `HTTP ${httpCode}` : 'Falha / Timeout'}</span>
      </span>
    );
  };

  return (
    <div className="space-y-6 animate-fade-in p-6 max-w-7xl mx-auto">
      <Breadcrumbs>
        <BreadcrumbItem href="/">Admin</BreadcrumbItem>
        <BreadcrumbItem href="/api-logs">Monitorização</BreadcrumbItem>
        <BreadcrumbItem>Webhooks & DLQ</BreadcrumbItem>
      </Breadcrumbs>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-black text-slate-900 dark:text-white flex items-center gap-2.5">
            <Radio className="w-7 h-7 text-primary-600" />
            <span>Central de Webhooks & Dead Letter Queue (DLQ)</span>
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Auditoria global de eventos disparados, análise de latência HTTP e recuperação de falhas
          </p>
        </div>

        <div className="flex items-center gap-2.5">
          <button
            type="button"
            onClick={() => refetch()}
            disabled={isFetching}
            className="btn-base btn-secondary text-xs px-3 py-2 flex items-center gap-1.5"
            title="Atualizar Registos"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${isFetching ? 'animate-spin text-primary-500' : ''}`} />
            <span>Atualizar</span>
          </button>

          {metrics.dlq_count > 0 && (
            <button
              type="button"
              onClick={() => batchRetryMutation.mutate(undefined)}
              disabled={batchRetryMutation.isPending}
              className="px-3.5 py-2 rounded-xl bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold shadow-xs transition-colors flex items-center gap-1.5 disabled:opacity-50"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${batchRetryMutation.isPending ? 'animate-spin' : ''}`} />
              <span>Reprocessar Toda a DLQ ({metrics.dlq_count})</span>
            </button>
          )}
        </div>
      </div>

      {/* Pulse KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Deliveries */}
        <div className="p-5 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-xs">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
              Total de Disparos
            </span>
            <div className="p-2 rounded-xl bg-primary-50 dark:bg-primary-950/50 text-primary-600 dark:text-primary-400">
              <Send className="w-4 h-4" />
            </div>
          </div>
          <div className="flex items-baseline justify-between mt-1">
            <span className="text-2xl font-extrabold text-slate-900 dark:text-white font-mono">
              {metrics.total.toLocaleString()}
            </span>
            <span className="text-2xs font-semibold text-slate-500 dark:text-slate-400">Histórico completo</span>
          </div>
        </div>

        {/* Successful Deliveries */}
        <div className="p-5 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-xs">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
              Entregas com Sucesso
            </span>
            <div className="p-2 rounded-xl bg-emerald-50 dark:bg-emerald-950/50 text-emerald-600 dark:text-emerald-400">
              <CheckCircle2 className="w-4 h-4" />
            </div>
          </div>
          <div className="flex items-baseline justify-between mt-1">
            <span className="text-2xl font-extrabold text-emerald-600 dark:text-emerald-400 font-mono">
              {metrics.successful.toLocaleString()}
            </span>
            <span className="text-2xs font-mono font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300">
              {metrics.success_rate}% Sucesso
            </span>
          </div>
        </div>

        {/* Failed Deliveries */}
        <div className="p-5 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-xs">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
              Falhas & Erros HTTP
            </span>
            <div className="p-2 rounded-xl bg-amber-50 dark:bg-amber-950/50 text-amber-600 dark:text-amber-400">
              <AlertTriangle className="w-4 h-4" />
            </div>
          </div>
          <div className="flex items-baseline justify-between mt-1">
            <span className="text-2xl font-extrabold text-amber-600 dark:text-amber-400 font-mono">
              {metrics.failed.toLocaleString()}
            </span>
            <span className="text-2xs font-semibold text-slate-500 dark:text-slate-400">Tentativas com erro</span>
          </div>
        </div>

        {/* Dead Letter Queue (DLQ) */}
        <div
          className={`p-5 rounded-2xl border shadow-xs transition-colors ${
            metrics.dlq_count > 0
              ? 'bg-rose-50/60 dark:bg-rose-950/20 border-rose-200 dark:border-rose-900/60'
              : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800'
          }`}
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
              Retidos na DLQ
            </span>
            <div
              className={`p-2 rounded-xl ${
                metrics.dlq_count > 0
                  ? 'bg-rose-100 text-rose-600 dark:bg-rose-900/60 dark:text-rose-400'
                  : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400'
              }`}
            >
              <AlertOctagon className="w-4 h-4" />
            </div>
          </div>
          <div className="flex items-baseline justify-between mt-1">
            <span
              className={`text-2xl font-extrabold font-mono ${
                metrics.dlq_count > 0
                  ? 'text-rose-600 dark:text-rose-400'
                  : 'text-slate-900 dark:text-white'
              }`}
            >
              {metrics.dlq_count}
            </span>
            {metrics.dlq_count > 0 ? (
              <span className="text-2xs font-mono font-bold px-2 py-0.5 rounded-full bg-rose-200 text-rose-800 dark:bg-rose-900 dark:text-rose-300">
                Ação Requerida
              </span>
            ) : (
              <span className="text-2xs font-semibold text-emerald-600 dark:text-emerald-400">
                Fila limpa
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Main Card with Tabs & Audit Table */}
      <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
        {/* Navigation Tabs */}
        <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-800 px-6 pt-4">
          <div className="flex items-center gap-6">
            <button
              type="button"
              onClick={() => {
                setActiveTab('ALL');
                setPage(0);
              }}
              className={`pb-3 font-bold text-sm border-b-2 transition-colors flex items-center gap-2 ${
                activeTab === 'ALL'
                  ? 'border-primary-600 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-200'
              }`}
            >
              <span>Todas as Entregas</span>
              <span className="text-2xs px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 font-mono">
                {metrics.total}
              </span>
            </button>

            <button
              type="button"
              onClick={() => {
                setActiveTab('DLQ');
                setPage(0);
              }}
              className={`pb-3 font-bold text-sm border-b-2 transition-colors flex items-center gap-2 ${
                activeTab === 'DLQ'
                  ? 'border-rose-600 text-rose-600 dark:text-rose-400'
                  : 'border-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-200'
              }`}
            >
              <AlertOctagon className="w-4 h-4 text-rose-500" />
              <span>Dead Letter Queue (DLQ)</span>
              {metrics.dlq_count > 0 && (
                <span className="text-2xs px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 dark:bg-rose-950 dark:text-rose-300 font-mono font-bold animate-pulse">
                  {metrics.dlq_count}
                </span>
              )}
            </button>
          </div>

          {/* Batch action button if items selected */}
          {selectedIds.length > 0 && (
            <div className="pb-3 flex items-center gap-2 animate-fade-in">
              <span className="text-xs text-slate-500 dark:text-slate-400 font-medium">
                {selectedIds.length} selecionado(s)
              </span>
              <button
                type="button"
                onClick={() => batchRetryMutation.mutate(selectedIds)}
                disabled={batchRetryMutation.isPending}
                className="btn-base btn-primary text-xs py-1.5 px-3 flex items-center gap-1.5"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${batchRetryMutation.isPending ? 'animate-spin' : ''}`} />
                <span>Reprocessar Selecionados</span>
              </button>
            </div>
          )}
        </div>

        {/* Filters Toolbar */}
        {activeTab === 'ALL' && (
          <div className="p-4 bg-slate-50/50 dark:bg-slate-800/40 border-b border-slate-200 dark:border-slate-800 flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-1 items-center gap-3 min-w-[240px] max-w-md">
              <div className="relative w-full">
                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Pesquisar por URL, evento ou ID..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setPage(0);
                  }}
                  className="w-full pl-9 pr-4 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(0);
                }}
                className="px-3 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="all">Todos os Estados</option>
                <option value="success">Sucesso (2xx)</option>
                <option value="failed">Falha / Erro</option>
                <option value="pending">Pendente</option>
              </select>

              <select
                value={eventTypeFilter}
                onChange={(e) => {
                  setEventTypeFilter(e.target.value);
                  setPage(0);
                }}
                className="px-3 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="all">Todos os Eventos</option>
                <option value="endpoint.created">endpoint.created</option>
                <option value="endpoint.updated">endpoint.updated</option>
                <option value="product.published">product.published</option>
                <option value="transaction.charged">transaction.charged</option>
                <option value="subscription.activated">subscription.activated</option>
                <option value="subscription.suspended">subscription.suspended</option>
              </select>
            </div>
          </div>
        )}

        {/* Table Content */}
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="bg-slate-50 dark:bg-slate-800/60 text-slate-500 dark:text-slate-400 uppercase tracking-wider font-bold border-b border-slate-200 dark:border-slate-800">
              <tr>
                <th className="py-3 px-4 w-10">
                  <input
                    type="checkbox"
                    onChange={handleSelectAll}
                    checked={deliveries.length > 0 && selectedIds.length === deliveries.length}
                    className="rounded border-slate-300 dark:border-slate-700 text-primary-600 focus:ring-primary-500"
                  />
                </th>
                <th className="py-3 px-4">Evento & Origem</th>
                <th className="py-3 px-4">Destino (Webhook URL)</th>
                <th className="py-3 px-4">Status HTTP</th>
                <th className="py-3 px-4">Tentativas</th>
                <th className="py-3 px-4">Data & Hora</th>
                <th className="py-3 px-4 text-right">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800/80">
              {isLoading ? (
                <tr>
                  <td colSpan={7} className="text-center py-12">
                    <RefreshCw className="w-6 h-6 text-primary-500 animate-spin mx-auto mb-2" />
                    <span className="text-xs text-slate-500 dark:text-slate-400">
                      Carregando eventos de webhook...
                    </span>
                  </td>
                </tr>
              ) : deliveries.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-12">
                    <Radio className="w-8 h-8 text-slate-400 mx-auto mb-2 opacity-50" />
                    <h4 className="text-sm font-bold text-slate-800 dark:text-slate-200">
                      Nenhum evento encontrado
                    </h4>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                      {activeTab === 'DLQ'
                        ? 'Excelente! A Dead Letter Queue está vazia e todas as entregas foram processadas.'
                        : 'Não há registos de webhooks correspondentes aos filtros selecionados.'}
                    </p>
                  </td>
                </tr>
              ) : (
                deliveries.map((item) => (
                  <tr
                    key={item.id}
                    className="hover:bg-slate-50/70 dark:hover:bg-slate-800/40 transition-colors"
                  >
                    <td className="py-3 px-4">
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(item.id)}
                        onChange={() => handleToggleSelect(item.id)}
                        className="rounded border-slate-300 dark:border-slate-700 text-primary-600 focus:ring-primary-500"
                      />
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex flex-col">
                        <span className="font-mono font-bold text-slate-900 dark:text-white">
                          {item.event_type}
                        </span>
                        <span className="text-2xs text-slate-500 dark:text-slate-400 mt-0.5 flex items-center gap-1">
                          <span className="font-semibold text-slate-700 dark:text-slate-300">
                            {item.source_name || 'Origem'}
                          </span>
                          <span>•</span>
                          <span>{item.source_type}</span>
                          {item.client_name && (
                            <>
                              <span>•</span>
                              <span className="text-primary-600 dark:text-primary-400">{item.client_name}</span>
                            </>
                          )}
                        </span>
                      </div>
                    </td>
                    <td className="py-3 px-4 max-w-xs">
                      <div className="flex items-center gap-1.5 font-mono text-slate-600 dark:text-slate-300 truncate">
                        <span className="truncate" title={item.webhook_url}>
                          {item.webhook_url}
                        </span>
                      </div>
                    </td>
                    <td className="py-3 px-4">{getStatusBadge(item.status, item.response_status)}</td>
                    <td className="py-3 px-4">
                      <span
                        className={`font-mono text-xs font-bold ${
                          item.attempts >= 3
                            ? 'text-rose-600 dark:text-rose-400'
                            : 'text-slate-700 dark:text-slate-300'
                        }`}
                      >
                        {item.attempts} / 3
                      </span>
                    </td>
                    <td className="py-3 px-4 text-slate-500 dark:text-slate-400 font-mono text-2xs">
                      {new Date(item.created_at).toLocaleString('pt-PT')}
                    </td>
                    <td className="py-3 px-4 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        <button
                          type="button"
                          onClick={() => setSelectedDelivery(item)}
                          className="p-1.5 rounded-lg text-slate-500 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                          title="Inspecionar Payload & Resposta"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => retryMutation.mutate(item.id)}
                          disabled={retryMutation.isPending}
                          className="p-1.5 rounded-lg text-slate-500 hover:text-emerald-600 dark:hover:text-emerald-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors disabled:opacity-50"
                          title="Re-tentar Envio"
                        >
                          <RefreshCw className={`w-4 h-4 ${retryMutation.isPending ? 'animate-spin' : ''}`} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Toolbar */}
        {activeTab === 'ALL' && totalPages > 1 && (
          <div className="p-4 border-t border-slate-200 dark:border-slate-800 flex items-center justify-between text-xs text-slate-500 dark:text-slate-400">
            <span>
              Apresentando <strong>{page * pageSize + 1}</strong> a{' '}
              <strong>{Math.min((page + 1) * pageSize, totalItems)}</strong> de{' '}
              <strong>{totalItems}</strong> registos
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
                className="px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 font-semibold disabled:opacity-40"
              >
                Anterior
              </button>
              <span className="font-mono">
                {page + 1} / {totalPages}
              </span>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
                className="px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 font-semibold disabled:opacity-40"
              >
                Seguinte
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Deep Inspection Modal */}
      {selectedDelivery && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-xs p-4 overflow-y-auto animate-fade-in"
          role="dialog"
          aria-modal="true"
        >
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 w-full max-w-2xl rounded-2xl shadow-2xl overflow-hidden my-8">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-5 border-b border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/40">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-xl bg-primary-100 dark:bg-primary-950/60 text-primary-600 dark:text-primary-400">
                  <Radio className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-slate-900 dark:text-white flex items-center gap-2">
                    <span>Inspeção de Evento de Webhook</span>
                    <span className="text-2xs font-mono px-2 py-0.5 rounded-full bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300">
                      {selectedDelivery.id.substring(0, 8)}...
                    </span>
                  </h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 font-mono">
                    {selectedDelivery.event_type} • {selectedDelivery.webhook_url}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setSelectedDelivery(null)}
                className="p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-800 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-5 sm:p-6 space-y-4 max-h-[70vh] overflow-y-auto text-xs">
              {/* Delivery Meta Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 bg-slate-50 dark:bg-slate-800/40 p-3.5 rounded-xl border border-slate-100 dark:border-slate-800">
                <div>
                  <span className="text-[10px] uppercase font-bold text-slate-400 block">Status HTTP</span>
                  <div className="mt-1">
                    {getStatusBadge(selectedDelivery.status, selectedDelivery.response_status)}
                  </div>
                </div>
                <div>
                  <span className="text-[10px] uppercase font-bold text-slate-400 block">Tentativas</span>
                  <span className="font-mono font-bold text-slate-800 dark:text-slate-200 mt-1 block">
                    {selectedDelivery.attempts} / 3
                  </span>
                </div>
                <div>
                  <span className="text-[10px] uppercase font-bold text-slate-400 block">Origem</span>
                  <span className="font-semibold text-slate-800 dark:text-slate-200 mt-1 block truncate">
                    {selectedDelivery.source_name}
                  </span>
                </div>
                <div>
                  <span className="text-[10px] uppercase font-bold text-slate-400 block">Data do Disparo</span>
                  <span className="font-mono text-slate-600 dark:text-slate-400 mt-1 block">
                    {new Date(selectedDelivery.created_at).toLocaleTimeString('pt-PT')}
                  </span>
                </div>
              </div>

              {/* Error Message if present */}
              {selectedDelivery.error_message && (
                <div className="p-3 rounded-xl bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-900/60">
                  <span className="text-[10px] uppercase font-bold text-rose-600 dark:text-rose-400 block mb-1">
                    Log de Erro da Comunicação
                  </span>
                  <p className="font-mono text-rose-800 dark:text-rose-200 text-xs break-all">
                    {selectedDelivery.error_message}
                  </p>
                </div>
              )}

              {/* Request Payload JSON */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <span className="font-bold text-slate-700 dark:text-slate-300">
                    Corpo da Requisição (Payload JSON)
                  </span>
                  <button
                    type="button"
                    onClick={() =>
                      copyToClipboard(
                        typeof selectedDelivery.payload === 'object'
                          ? JSON.stringify(selectedDelivery.payload, null, 2)
                          : String(selectedDelivery.payload),
                        'payload'
                      )
                    }
                    className="text-2xs font-semibold text-primary-600 dark:text-primary-400 hover:underline flex items-center gap-1"
                  >
                    {copiedKey === 'payload' ? (
                      <Check className="w-3 h-3 text-emerald-500" />
                    ) : (
                      <Copy className="w-3 h-3" />
                    )}
                    <span>{copiedKey === 'payload' ? 'Copiado!' : 'Copiar'}</span>
                  </button>
                </div>
                <pre className="p-3 rounded-xl bg-slate-900 text-slate-100 font-mono text-2xs overflow-x-auto max-h-48 border border-slate-800">
                  {typeof selectedDelivery.payload === 'object'
                    ? JSON.stringify(selectedDelivery.payload, null, 2)
                    : selectedDelivery.payload}
                </pre>
              </div>

              {/* Response Body if present */}
              {selectedDelivery.response_body && (
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-slate-700 dark:text-slate-300">
                      Resposta do Servidor de Destino
                    </span>
                    <button
                      type="button"
                      onClick={() => copyToClipboard(selectedDelivery.response_body || '', 'response')}
                      className="text-2xs font-semibold text-primary-600 dark:text-primary-400 hover:underline flex items-center gap-1"
                    >
                      {copiedKey === 'response' ? (
                        <Check className="w-3 h-3 text-emerald-500" />
                      ) : (
                        <Copy className="w-3 h-3" />
                      )}
                      <span>{copiedKey === 'response' ? 'Copiado!' : 'Copiar'}</span>
                    </button>
                  </div>
                  <pre className="p-3 rounded-xl bg-slate-100 dark:bg-slate-800/80 text-slate-800 dark:text-slate-200 font-mono text-2xs overflow-x-auto max-h-36 border border-slate-200 dark:border-slate-700">
                    {selectedDelivery.response_body}
                  </pre>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/40 flex items-center justify-between gap-3">
              <button
                type="button"
                onClick={() => setSelectedDelivery(null)}
                className="px-3 py-1.5 text-xs font-semibold rounded-xl text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              >
                Fechar
              </button>
              <button
                type="button"
                onClick={() => {
                  retryMutation.mutate(selectedDelivery.id);
                  setSelectedDelivery(null);
                }}
                disabled={retryMutation.isPending}
                className="btn-base btn-primary text-xs py-1.5 px-3.5 flex items-center gap-1.5"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${retryMutation.isPending ? 'animate-spin' : ''}`} />
                <span>Re-tentar Entrega Agora</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
